/**
 * SPCB Shipping Validator Module v1.10.0
 *
 * Handles:
 * - Checkout validation for shipping fields
 * - Branch/Other shipping validation modals
 * - Field label cleanup (remove "opcional")
 * - On-blur validation with visual feedback
 *
 * @package SuperPymeCheckoutBlocks
 */

// Import shared utilities from parent module
const getLog = () => window.spcbShippingUtils?.log || ((...args) => {});
const getIcons = () => window.spcbShippingUtils?.ICONS || {};

// Import from other modules
import {
    getOtherData,
    isOtherShippingSelected,
    createOtherModal
} from './other-modal.js';

let checkoutValidationInitialized = false;
let lastPickupState = null;

const isCheckoutProcessing = () => window.spcbCheckoutLock?.isProcessing?.() === true;

// ============================================
// LOCAL PICKUP DETECTION
// ============================================

/**
 * Check if local pickup is selected (no shipping address required)
 *
 * SIMPLE & RELIABLE DETECTION:
 * WooCommerce Blocks has two options: "Enviar" (Ship) and a configurable pickup name.
 * "Enviar" is ALWAYS the shipping option (constant in WC Blocks).
 * If the selected option is NOT "Enviar", it's local pickup.
 */
export function isLocalPickupModeSelected() {
    const log = getLog();

    // PRIMARY METHOD: Check the selected shipping method option
    // "Enviar" is the constant name for shipping in WC Blocks
    // Anything else selected = local pickup
    const selectedOption = document.querySelector('.wc-block-checkout__shipping-method-option[aria-checked="true"], .wc-block-checkout__shipping-method-option--selected');
    if (selectedOption) {
        const title = selectedOption.querySelector('.wc-block-checkout__shipping-method-option-title');
        if (title) {
            const text = title.textContent.trim();
            // If it's NOT "Enviar", it's local pickup (whatever the store named it)
            if (text.toLowerCase() !== 'enviar') {
                log('Local pickup detected: selected option is "' + text + '" (not "Enviar")');
                return true;
            } else {
                log('Shipping mode: "Enviar" is selected');
                return false;
            }
        }
    }

    // FALLBACK: If no shipping method toggle exists, check other indicators
    // This handles cases where only one delivery method is available

    // Check if shipping fields section is hidden/removed
    const shippingFields = document.querySelector('.wc-block-checkout__shipping-fields');
    if (shippingFields) {
        const style = window.getComputedStyle(shippingFields);
        if (style.display === 'none' || shippingFields.hidden) {
            log('Local pickup detected: shipping fields are hidden');
            return true;
        }
    }

    // Check wp.data store
    if (window.wp?.data?.select) {
        try {
            const checkout = window.wp.data.select('wc/store/checkout');
            if (checkout?.prefersCollection?.()) {
                log('Local pickup detected via wp.data prefersCollection');
                return true;
            }
        } catch (e) {
            // wp.data not available
        }
    }

    // Check selected shipping rate ID as last resort
    const selectedRadio = document.querySelector('.wc-block-components-shipping-rates-control input[type="radio"]:checked');
    if (selectedRadio) {
        const rateId = selectedRadio.value || '';
        if (rateId.includes('local_pickup') || rateId.includes('pickup_location')) {
            log('Local pickup detected via rate ID:', rateId);
            return true;
        }
    }

    log('Shipping mode detected (no pickup indicators found)');
    return false;
}

// ============================================
// SHIPPING ADDRESS VALIDATION
// ============================================

/**
 * Check if branch mode is active (checkbox checked)
 * v1.17.0: Branch mode hides address_1 field
 */
function isBranchModeActive() {
    // Check checkbox state
    const branchCheckbox = document.getElementById('spcb-branch-checkbox');
    if (branchCheckbox && branchCheckbox.checked) {
        return true;
    }
    // Also check body class as fallback
    if (document.body.classList.contains('spcb-branch-mode')) {
        return true;
    }
    return false;
}

/**
 * Validate shipping address fields before checkout
 * Returns array of missing field labels, empty if all valid
 */
export function validateShippingAddressFields() {
    // Skip validation for local pickup
    if (isLocalPickupModeSelected()) {
        getLog()('Local pickup selected, skipping shipping address validation');
        return [];
    }

    const missingFields = [];
    const isBranchMode = isBranchModeActive();

    // Required shipping fields
    // v1.11.5: Removed shipping-first_name and shipping-last_name
    // Names are now validated via WooCommerce Additional Checkout Fields API
    // in contact section (spcb/contact_first_name, spcb/contact_last_name)
    // v1.17.0: address_1 is NOT required in branch mode (it's hidden and auto-filled)
    const requiredFields = [
        { id: 'shipping-state', label: 'Región' },
        { id: 'shipping-city', label: 'Comuna' },
    ];

    // Only require address_1 when NOT in branch mode
    if (!isBranchMode) {
        requiredFields.unshift({ id: 'shipping-address_1', label: 'Dirección' });
    } else {
        getLog()('Branch mode active, skipping address_1 validation');
    }

    requiredFields.forEach(field => {
        const input = document.getElementById(field.id);
        if (!input) {
            getLog()('Field not found:', field.id);
            return;
        }

        const value = input.value?.trim() || '';
        if (!value) {
            missingFields.push(field.label);
            // Add visual error class
            const wrapper = input.closest('.wc-block-components-text-input, .wc-block-components-select-input, .wc-blocks-components-select');
            if (wrapper) {
                wrapper.classList.add('has-error');
                input.setAttribute('aria-invalid', 'true');
            }
        } else {
            // Remove error class if value exists
            const wrapper = input.closest('.wc-block-components-text-input, .wc-block-components-select-input, .wc-blocks-components-select');
            if (wrapper) {
                wrapper.classList.remove('has-error');
                input.setAttribute('aria-invalid', 'false');
            }
        }
    });

    // Also check comuna select (SPCB custom)
    const comunaSelect = document.getElementById('shipping-city-spcb');
    if (comunaSelect) {
        const comunaValue = comunaSelect.value?.trim() || '';
        if (!comunaValue && !missingFields.includes('Comuna')) {
            missingFields.push('Comuna');
            const wrapper = comunaSelect.closest('.wc-block-components-text-input, .spcb-comuna-active');
            if (wrapper) {
                wrapper.classList.add('has-error');
            }
        }
    }

    return missingFields;
}

/**
 * Show validation error message for shipping fields
 */
export function showShippingValidationError(missingFields) {
    const ICONS = getIcons();

    // Remove any existing error
    const existingError = document.querySelector('.spcb-shipping-validation-error');
    if (existingError) {
        existingError.remove();
    }

    // Find shipping section
    const shippingSection = document.querySelector('#shipping-fields, .wc-block-checkout__shipping-fields');
    if (!shippingSection) return;

    // Create error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'spcb-shipping-validation-error wc-block-components-validation-error';
    errorDiv.setAttribute('role', 'alert');
    errorDiv.innerHTML = `
        <div style="
            background: #fef2f2;
            border: 1px solid #fecaca;
            border-radius: 6px;
            padding: 12px 16px;
            margin-top: -20px;
            margin-bottom: 12px;
            color: #991b1b;
            font-size: 14px;
        ">
            <strong>${ICONS.alertTriangle || ''} Campos obligatorios:</strong><br>
            Por favor completa: ${missingFields.join(', ')}
        </div>
    `;

    // Insert at the beginning of shipping section content
    const content = shippingSection.querySelector('.wc-block-components-checkout-step__content');
    if (content) {
        content.insertBefore(errorDiv, content.firstChild);
    } else {
        shippingSection.insertBefore(errorDiv, shippingSection.firstChild);
    }

    // Scroll to error
    errorDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });

    // Focus first invalid field
    const firstInvalidWrapper = shippingSection.querySelector('.has-error');
    if (firstInvalidWrapper) {
        const input = firstInvalidWrapper.querySelector('input, select');
        if (input) {
            setTimeout(() => input.focus(), 100);
        }
    }

    getLog()('Shipping validation error shown:', missingFields);
}

/**
 * Clear shipping validation errors
 */
export function clearShippingValidationErrors() {
    const errorDiv = document.querySelector('.spcb-shipping-validation-error');
    if (errorDiv) {
        errorDiv.remove();
    }

    // Clear field error classes
    document.querySelectorAll('.wc-block-components-text-input.has-error, .wc-blocks-components-select.has-error, .spcb-comuna-active.has-error').forEach(el => {
        el.classList.remove('has-error');
        const input = el.querySelector('input, select');
        if (input) {
            input.setAttribute('aria-invalid', 'false');
        }
    });
}

// ============================================
// VALIDATION MODALS
// ============================================

function showBranchValidationBanner() {
    if (document.querySelector('.spcb-branch-validation-banner')) {
        return;
    }

    const ICONS = getIcons();
    const banner = document.createElement('div');
    banner.className = 'spcb-branch-validation-banner';
    banner.innerHTML = `
        <div style="
            background: #fff3cd;
            border: 1px solid #ffc107;
            border-radius: 6px;
            padding: 10px 12px;
            margin: 12px 0 8px;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            color: #856404;
        ">
            <span style="display:flex;align-items:center;">${ICONS.alertTriangle || '⚠️'}</span>
            <span>Revisa los datos de envío a sucursal antes de continuar.</span>
        </div>
    `;

    const referenceContainer = document.querySelector('.spcb-reference-field-container');
    const checkboxContainer = document.querySelector('.spcb-branch-checkbox-container');
    const anchor = referenceContainer || checkboxContainer || document.querySelector('.wc-block-components-shipping-rates-control');
    if (!anchor) {
        return;
    }

    anchor.insertAdjacentElement('afterend', banner);
    getLog()('Branch validation banner shown');
}

export function showBranchValidationModal() {
    showBranchValidationBanner();
}

export function showOtherValidationModal() {
    const ICONS = getIcons();
    createOtherModal();
    const modal = document.getElementById('spcb-other-modal');
    if (!modal) return;

    let warningEl = modal.querySelector('.spcb-validation-warning');
    if (!warningEl) {
        warningEl = document.createElement('div');
        warningEl.className = 'spcb-validation-warning';
        warningEl.innerHTML = `
            <div class="spcb-warning-icon">${ICONS.alertTriangle || ''}</div>
            <div class="spcb-warning-text">Debes indicar el nombre del transporte antes de continuar.</div>
        `;
        const modalBody = modal.querySelector('.spcb-modal-body');
        if (modalBody) {
            modalBody.insertBefore(warningEl, modalBody.firstChild);
        }
    }
    warningEl.style.display = 'flex';

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    setTimeout(() => {
        modal.querySelector('#spcb-other-transport')?.focus();
    }, 100);

    getLog()('Other validation modal shown');
}

// ============================================
// CHECKOUT VALIDATION INITIALIZATION
// ============================================

export function initCheckoutValidation() {
    if (checkoutValidationInitialized) return;
    checkoutValidationInitialized = true;

    const log = getLog();

    document.addEventListener('click', (e) => {
        const placeOrderBtn = e.target.closest('.wc-block-components-checkout-place-order-button');
        if (!placeOrderBtn) return;

        const config = window.spcbConfig || {};

        // Check if local pickup mode is selected
        const isLocalPickup = isLocalPickupModeSelected();
        log('Place order clicked, isLocalPickup:', isLocalPickup);

        // FIRST: Validate shipping address fields (unless local pickup)
        if (isLocalPickup) {
            log('Local pickup mode - skipping shipping address validation');
            clearShippingValidationErrors();
        } else {
            const missingShippingFields = validateShippingAddressFields();
            if (missingShippingFields.length > 0) {
                e.preventDefault();
                e.stopPropagation();
                showShippingValidationError(missingShippingFields);
                log('Blocked checkout - missing shipping fields:', missingShippingFields);
                return false;
            } else {
                clearShippingValidationErrors();
            }
        }

        // Validate "Otro" shipping data
        if (isOtherShippingSelected() && config.otherShippingEnabled) {
            const otherData = getOtherData();
            if (!otherData || !otherData.transportName) {
                e.preventDefault();
                e.stopPropagation();
                showOtherValidationModal();
                return false;
            }
        }
    }, true);

    // Clear errors when user starts filling fields
    document.addEventListener('input', (e) => {
        if (e.target.id?.startsWith('shipping-')) {
            const wrapper = e.target.closest('.wc-block-components-text-input, .wc-blocks-components-select');
            if (wrapper && wrapper.classList.contains('has-error')) {
                wrapper.classList.remove('has-error');
                e.target.setAttribute('aria-invalid', 'false');
            }
            // Also clear inline styles from on-blur validation
            e.target.style.borderColor = '';
            e.target.style.boxShadow = '';
        }
    });

    // Clear errors when comuna select changes
    document.addEventListener('change', (e) => {
        if (e.target.id === 'shipping-city-spcb' || e.target.id === 'shipping-state') {
            const wrapper = e.target.closest('.wc-block-components-text-input, .wc-blocks-components-select, .spcb-comuna-active');
            if (wrapper) {
                wrapper.classList.remove('has-error');
            }
        }
    });

    // ON BLUR validation - show red border immediately when leaving empty required field
    // Uses INLINE STYLES to bypass any CSS specificity issues
    document.addEventListener('focusout', (e) => {
        // Skip if local pickup selected
        if (isLocalPickupModeSelected()) return;

        const target = e.target;
        const fieldId = target.id || '';

        // Only validate shipping fields
        if (!fieldId.startsWith('shipping-')) return;

        // List of required shipping field IDs
        const requiredFieldIds = [
            'shipping-first_name',
            'shipping-last_name',
            'shipping-address_1',
            'shipping-state',
            'shipping-city',
            'shipping-city-spcb'
        ];

        if (!requiredFieldIds.includes(fieldId)) return;

        const value = target.value?.trim() || '';

        if (!value) {
            // EMPTY: Apply red border using INLINE STYLE for guaranteed visibility
            target.style.borderColor = '#dc2626';
            target.style.boxShadow = '0 0 0 1px #dc2626';
            target.setAttribute('aria-invalid', 'true');
            getLog()('On blur: empty field, applied inline error style to', fieldId);
        } else {
            // HAS VALUE: Remove inline error styles
            target.style.borderColor = '';
            target.style.boxShadow = '';
            target.setAttribute('aria-invalid', 'false');
        }
    }, true);

    getLog()('Checkout validation initialized (with shipping fields + on blur)');
}

// ============================================
// SHIPPING FIELD LABELS CLEANUP
// ============================================

/**
 * Remove "(opcional)" from shipping field labels
 * This is needed because woocommerce_get_country_locale makes fields optional
 * for billing purposes, but we validate shipping fields manually.
 *
 * Uses requestAnimationFrame to run AFTER React's render cycle completes.
 */
export function cleanShippingFieldLabels() {
    // Use RAF to ensure we run after React's render
    requestAnimationFrame(() => {
        // Find shipping section - try multiple selectors
        const shippingSection = document.querySelector(
            '#shipping-fields, ' +
            '.wc-block-checkout__shipping-fields, ' +
            '#shipping .wc-block-components-address-form, ' +
            '.wc-block-checkout__shipping-fields .wc-block-components-address-form'
        );

        // If no shipping section found, search the whole document for shipping labels
        const searchContainer = shippingSection || document;

        // Fields that should NOT show "(opcional)" in shipping
        const requiredFields = ['first_name', 'last_name', 'address_1', 'city', 'state'];
        let cleanedCount = 0;

        requiredFields.forEach(fieldName => {
            // Find ALL labels for shipping fields (multiple selectors for robustness)
            const selectors = [
                `label[for="shipping-${fieldName}"]`,
                `label[for="shipping-${fieldName.replace('_', '-')}"]`,
                `.wc-block-components-address-form__${fieldName} label`,
                `[class*="address-form__${fieldName}"] label`
            ];

            selectors.forEach(selector => {
                const labels = searchContainer.querySelectorAll(selector);
                labels.forEach(label => {
                    // Check if this is actually a shipping label (not billing)
                    const forAttr = label.getAttribute('for') || '';
                    const isShippingLabel = forAttr.startsWith('shipping') ||
                                           label.closest('#shipping') ||
                                           label.closest('.wc-block-checkout__shipping-fields') ||
                                           label.closest('[id*="shipping"]');

                    if (!isShippingLabel && forAttr.startsWith('billing')) {
                        return; // Skip billing labels
                    }

                    // Remove "(opcional)" from label text content
                    const optionalRegex = /\s*\(opcional\)/gi;
                    if (label.textContent && optionalRegex.test(label.textContent)) {
                        // Store original text and clean it
                        const cleanedText = label.textContent.replace(optionalRegex, '').trim();

                        // Only update if text actually changed
                        if (label.textContent !== cleanedText) {
                            label.textContent = cleanedText;
                            cleanedCount++;
                            getLog()('Cleaned label for:', fieldName, '→', cleanedText);
                        }
                    }
                });
            });

            // Also clean any input aria-labels
            const inputs = searchContainer.querySelectorAll(`#shipping-${fieldName}, [id*="shipping"][id*="${fieldName}"]`);
            inputs.forEach(input => {
                const ariaLabel = input.getAttribute('aria-label');
                if (ariaLabel && /\(opcional\)/i.test(ariaLabel)) {
                    input.setAttribute('aria-label', ariaLabel.replace(/\s*\(opcional\)/gi, ''));
                }
            });
        });

        if (cleanedCount > 0) {
            getLog()('Cleaned', cleanedCount, 'shipping field labels');
        }
    });
}

/**
 * Initialize shipping field label cleanup with aggressive observer
 */
export function initShippingFieldLabels() {
    // Initial cleanup with multiple delays (React may render in multiple passes)
    cleanShippingFieldLabels();
    setTimeout(cleanShippingFieldLabels, 100);
    setTimeout(cleanShippingFieldLabels, 300);
    setTimeout(cleanShippingFieldLabels, 500);
    setTimeout(cleanShippingFieldLabels, 1000);
    setTimeout(cleanShippingFieldLabels, 2000);
    setTimeout(cleanShippingFieldLabels, 3000);
    setTimeout(cleanShippingFieldLabels, 5000);

    // Observe for dynamic changes with characterData to catch text changes
    const checkoutForm = document.querySelector('.wc-block-checkout, .wc-block-checkout__form, #checkout');
    if (checkoutForm) {
        const observer = new MutationObserver((mutations) => {
            // Check if any mutation involves label text
            let shouldClean = false;
            for (const mutation of mutations) {
                if (mutation.type === 'characterData') {
                    shouldClean = true;
                    break;
                }
                if (mutation.type === 'childList') {
                    // Check if added nodes contain labels or form fields
                    for (const node of mutation.addedNodes) {
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            if (node.tagName === 'LABEL' ||
                                node.querySelector?.('label') ||
                                node.classList?.contains('wc-block-components-address-form')) {
                                shouldClean = true;
                                break;
                            }
                        }
                    }
                }
                if (shouldClean) break;
            }

            if (shouldClean) {
                // Debounce but keep it fast
                clearTimeout(window.spcbLabelCleanupDebounce);
                window.spcbLabelCleanupDebounce = setTimeout(cleanShippingFieldLabels, 50);
            }
        });

        observer.observe(checkoutForm, {
            childList: true,
            subtree: true,
            characterData: true,
            characterDataOldValue: true
        });

        getLog()('Shipping field labels observer initialized (aggressive mode)');
    }

    // Also listen for focus events on shipping fields - React may update labels on focus
    document.addEventListener('focusin', (e) => {
        if (e.target.id?.startsWith('shipping-')) {
            setTimeout(cleanShippingFieldLabels, 10);
            setTimeout(cleanShippingFieldLabels, 100);
        }
    });

    // Listen for country/state changes which may cause React to re-render
    document.addEventListener('change', (e) => {
        if (e.target.id?.includes('shipping') &&
            (e.target.id?.includes('country') || e.target.id?.includes('state'))) {
            setTimeout(cleanShippingFieldLabels, 100);
            setTimeout(cleanShippingFieldLabels, 500);
            setTimeout(cleanShippingFieldLabels, 1000);
        }
    });
}

// ============================================
// LOCAL PICKUP BODY CLASS TOGGLE
// ============================================

/**
 * Update body class based on local pickup selection.
 * This is used by CSS to show/hide billing address block.
 *
 * WooCommerce Blocks needs at least ONE address form visible:
 * - With "Enviar": shipping is visible → hide billing
 * - With local pickup: shipping is hidden → show billing
 */
export function updateLocalPickupBodyClass() {
    const isPickup = isLocalPickupModeSelected();
    const body = document.body;

    if (isPickup !== lastPickupState) {
        lastPickupState = isPickup;
        if (isPickup) {
            autoFillBillingFromContact();
            setTimeout(autoFillBillingFromContact, 100);
        }
    }

    if (isPickup) {
        if (!body.classList.contains('spcb-local-pickup-active')) {
            body.classList.add('spcb-local-pickup-active');
            getLog()('Added spcb-local-pickup-active class to body');
        }
    } else {
        if (body.classList.contains('spcb-local-pickup-active')) {
            body.classList.remove('spcb-local-pickup-active');
            getLog()('Removed spcb-local-pickup-active class from body');
        }
    }
}

/**
 * Initialize local pickup body class observer.
 * Watches for shipping method changes and updates body class accordingly.
 */
export function initLocalPickupBodyClass() {
    const log = getLog();

    // Initial update
    updateLocalPickupBodyClass();
    setTimeout(updateLocalPickupBodyClass, 100);
    setTimeout(updateLocalPickupBodyClass, 500);
    setTimeout(updateLocalPickupBodyClass, 1000);

    // Watch for clicks on shipping method options
    document.addEventListener('click', (e) => {
        const shippingOption = e.target.closest('.wc-block-checkout__shipping-method-option');
        if (shippingOption) {
            // Small delay to let React update the state
            setTimeout(updateLocalPickupBodyClass, 50);
            setTimeout(updateLocalPickupBodyClass, 200);
        }
    });

    // Watch for changes in shipping rates radio buttons
    document.addEventListener('change', (e) => {
        if (e.target.closest('.wc-block-components-shipping-rates-control')) {
            setTimeout(updateLocalPickupBodyClass, 50);
            setTimeout(updateLocalPickupBodyClass, 200);
        }
    });

    // Observe checkout container for shipping method changes
    const checkout = document.querySelector('.wc-block-checkout, .wc-block-checkout__form');
    if (checkout) {
        const observer = new MutationObserver(() => {
            updateLocalPickupBodyClass();
        });

        observer.observe(checkout, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['aria-checked', 'class']
        });

        log('Local pickup body class observer initialized');
    }

    // Also use wp.data subscribe if available
    if (window.wp?.data?.subscribe) {
        try {
            let lastPickupState = null;
            window.wp.data.subscribe(() => {
                const isPickup = isLocalPickupModeSelected();
                if (isPickup !== lastPickupState) {
                    lastPickupState = isPickup;
                    updateLocalPickupBodyClass();
                }
            });
            log('wp.data subscription for local pickup initialized');
        } catch (e) {
            // wp.data not available
        }
    }
}

// ============================================
// AUTO-FILL BILLING FROM CONTACT
// ============================================

/**
 * Auto-fill billing ADDRESS fields from shipping info (v1.10.45)
 *
 * Note: Billing names (first_name, last_name) are now native WooCommerce fields
 * that are always visible in the checkout. We only need to fill ADDRESS fields.
 *
 * This is needed because billing address fields are hidden but WooCommerce Blocks
 * may still validate them. By pre-filling, we ensure validation passes.
 */
export function autoFillBillingFromContact() {
    const log = getLog();

    if (isCheckoutProcessing()) {
        log('Auto-fill billing skipped - checkout processing');
        return;
    }

    // Get shipping address info
    const phone = document.getElementById('phone')?.value ||
                  document.getElementById('shipping-phone')?.value ||
                  document.getElementById('shipping-phone-shadow')?.value || '';
    const shippingAddress1 = document.getElementById('shipping-address_1')?.value || 'Retiro en tienda';
    const shippingCity = document.getElementById('shipping-city')?.value ||
                         document.getElementById('shipping-city-spcb')?.value || 'Santiago';
    const shippingState = document.getElementById('shipping-state')?.value || 'CL-RM';

    // Set billing fields helper
    const setFieldValue = (id, value) => {
        const field = document.getElementById(id);
        if (field && !field.value) {
            field.value = value;
            // Trigger input event for React to pick up the change
            field.dispatchEvent(new Event('input', { bubbles: true }));
            field.dispatchEvent(new Event('change', { bubbles: true }));
            log('Auto-filled billing field:', id, '=', value);
        }
    };

    // Fill billing ADDRESS fields (NOT names - those are native WC fields now)
    setFieldValue('billing-address_1', shippingAddress1);
    setFieldValue('billing-city', shippingCity);
    setFieldValue('billing-state', shippingState);
    setFieldValue('billing-phone', phone || '999999999');

    // Also update the SPCB comuna select if present
    const billingComunaSpcb = document.getElementById('billing-city-spcb');
    if (billingComunaSpcb && !billingComunaSpcb.value && shippingCity) {
        billingComunaSpcb.value = shippingCity;
        billingComunaSpcb.dispatchEvent(new Event('change', { bubbles: true }));
    }

    log('Auto-fill billing address completed:', { shippingAddress1, shippingCity });
}

/**
 * Auto-fill shipping names from billing (v1.10.45)
 *
 * When shipping form appears (user selects "Enviar"), pre-fill names from billing.
 * This saves the user from typing their name twice.
 */
export function autoFillShippingFromBilling() {
    const log = getLog();

    if (isCheckoutProcessing()) {
        log('Auto-fill shipping skipped - checkout processing');
        return;
    }

    const billingFirst = document.getElementById('billing-first_name')?.value || '';
    const billingLast = document.getElementById('billing-last_name')?.value || '';
    const shippingFirst = document.getElementById('shipping-first_name');
    const shippingLast = document.getElementById('shipping-last_name');

    // Only fill if shipping is empty and billing has values
    if (billingFirst && shippingFirst && !shippingFirst.value) {
        shippingFirst.value = billingFirst;
        shippingFirst.dispatchEvent(new Event('input', { bubbles: true }));
        shippingFirst.dispatchEvent(new Event('change', { bubbles: true }));
        log('Auto-filled shipping first_name from billing:', billingFirst);
    }

    if (billingLast && shippingLast && !shippingLast.value) {
        shippingLast.value = billingLast;
        shippingLast.dispatchEvent(new Event('input', { bubbles: true }));
        shippingLast.dispatchEvent(new Event('change', { bubbles: true }));
        log('Auto-filled shipping last_name from billing:', billingLast);
    }
}

/**
 * Initialize auto-fill logic for billing and shipping (v1.10.45)
 *
 * - Fills billing ADDRESS from shipping (for local pickup, address is placeholder)
 * - Fills shipping NAMES from billing (saves user from typing name twice)
 */
export function initAutoFillBilling() {
    const log = getLog();

    // Helper to run both auto-fills
    const runAutoFills = () => {
        autoFillBillingFromContact();
        autoFillShippingFromBilling();
    };

    // Fill on page load (after a delay for React to render)
    setTimeout(runAutoFills, 1000);
    setTimeout(runAutoFills, 2000);
    setTimeout(runAutoFills, 3000);

    // Fill when billing name fields change (to propagate to shipping)
    ['billing-first_name', 'billing-last_name'].forEach(id => {
        const field = document.getElementById(id);
        if (field) {
            field.addEventListener('change', () => {
                setTimeout(autoFillShippingFromBilling, 100);
            });
            field.addEventListener('blur', () => {
                setTimeout(autoFillShippingFromBilling, 100);
            });
        }
    });

    // Fill when shipping address changes (to propagate to billing)
    ['shipping-address_1', 'shipping-city', 'shipping-city-spcb', 'shipping-state', 'shipping-phone'].forEach(id => {
        const field = document.getElementById(id);
        if (field) {
            field.addEventListener('change', () => {
                setTimeout(autoFillBillingFromContact, 100);
            });
        }
    });

    log('Auto-fill initialization complete (v1.10.45)');
}

// ============================================
// PRE-FILL CONTACT FIELDS FROM BILLING
// ============================================

/**
 * Set input value in a React-aware way (v1.16.26)
 *
 * Uses Object.getOwnPropertyDescriptor to access the native value setter,
 * which properly triggers React's synthetic event system. This pattern is
 * already proven in phone-input.js and checkout-store-sync.js.
 *
 * @param {HTMLInputElement} input - The input element to update
 * @param {string} value - The value to set
 * @returns {boolean} True if value was set successfully
 */
function setInputValueReact(input, value) {
    if (!input || !value) return false;

    // Skip if already has this value
    if (input.value === value) return false;

    // Try to get the native value setter (bypasses React's controlled input)
    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLInputElement.prototype,
        'value'
    )?.set;

    if (nativeInputValueSetter) {
        // Use native setter to update value (React will detect this)
        nativeInputValueSetter.call(input, value);

        // Dispatch events to notify React and other listeners
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));

        return true;
    }

    // Fallback: direct assignment (may not trigger React in all cases)
    input.value = value;
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
}

/**
 * Pre-fill custom contact fields from WooCommerce billing data (v1.11.1)
 *
 * WooCommerce pre-fills native billing fields automatically for logged-in users.
 * Our custom contact fields (contact_first_name, contact_last_name, contact_phone)
 * need to be manually populated from those billing values.
 *
 * v1.16.26: Uses setInputValueReact() for proper React state synchronization.
 *
 * This runs on page load to copy:
 * - billing_first_name → contact_first_name
 * - billing_last_name → contact_last_name
 * - billing_phone → contact_phone
 */
export function prefillContactFieldsFromBilling() {
    const log = getLog();

    if (isCheckoutProcessing()) {
        log('Pre-fill contact skipped - checkout processing');
        return;
    }

    // Get billing field values (pre-filled by WooCommerce for logged-in users)
    const billingFirstName = document.getElementById('billing-first_name')?.value || '';
    const billingLastName = document.getElementById('billing-last_name')?.value || '';
    const billingPhone = document.getElementById('billing-phone')?.value ||
                         document.getElementById('phone')?.value || '';

    // Get contact field elements
    const contactFirstName = document.getElementById('contact-spcb-contact_first_name');
    const contactLastName = document.getElementById('contact-spcb-contact_last_name');
    const contactPhone = document.getElementById('contact-spcb-contact_phone');
    const contactPhoneShadow = document.getElementById('contact-spcb-contact_phone-shadow');

    // Fill contact first name if billing has value and contact is empty
    if (billingFirstName && contactFirstName && !contactFirstName.value) {
        if (setInputValueReact(contactFirstName, billingFirstName)) {
            log('Pre-filled contact_first_name from billing:', billingFirstName);
        }
    }

    // Fill contact last name
    if (billingLastName && contactLastName && !contactLastName.value) {
        if (setInputValueReact(contactLastName, billingLastName)) {
            log('Pre-filled contact_last_name from billing:', billingLastName);
        }
    }

    // Fill contact phone (both shadow and hidden field if using intl-tel-input)
    if (billingPhone) {
        // Fill the shadow input (visible field with intl-tel-input)
        if (contactPhoneShadow && !contactPhoneShadow.value) {
            if (setInputValueReact(contactPhoneShadow, billingPhone)) {
                log('Pre-filled contact_phone shadow from billing:', billingPhone);

                // Trigger intl-tel-input to update country detection
                const iti = window.intlTelInputGlobals?.getInstance?.(contactPhoneShadow);
                if (iti) {
                    iti.setNumber(billingPhone);
                    log('Updated intl-tel-input with billing phone');
                }
            }
        }

        // Also fill the hidden field directly
        if (contactPhone && !contactPhone.value) {
            setInputValueReact(contactPhone, billingPhone);
        }
    }

    log('Pre-fill contact fields from billing completed');
}

/**
 * Initialize contact field pre-fill from billing data (v1.11.1)
 *
 * Runs after page load with multiple delays to ensure:
 * 1. React has rendered all fields
 * 2. WooCommerce has pre-filled billing fields
 * 3. intl-tel-input is initialized
 *
 * v1.16.26: Reduced from 5 timeouts to 3 optimized delays.
 * With setInputValueReact() properly syncing with React, we need fewer retries.
 */
export function initPrefillContactFields() {
    const log = getLog();

    // v1.16.26: Optimized delays - 3 instead of 5, with better timing
    // 500ms: First render usually complete
    // 1500ms: WC async updates typically done
    // 3000ms: Final fallback for slow connections
    setTimeout(prefillContactFieldsFromBilling, 500);
    setTimeout(prefillContactFieldsFromBilling, 1500);
    setTimeout(prefillContactFieldsFromBilling, 3000);

    // Also watch for billing field changes (in case user updates billing later)
    ['billing-first_name', 'billing-last_name', 'billing-phone'].forEach(id => {
        const field = document.getElementById(id);
        if (field) {
            field.addEventListener('change', () => {
                setTimeout(prefillContactFieldsFromBilling, 100);
            });
        }
    });

    // Watch for wp.data store changes (WooCommerce may update fields asynchronously)
    if (window.wp?.data?.subscribe) {
        try {
            let checkCount = 0;
            const maxChecks = 10;

            const unsubscribe = window.wp.data.subscribe(() => {
                if (checkCount < maxChecks) {
                    checkCount++;
                    prefillContactFieldsFromBilling();
                } else {
                    // Stop subscribing after enough checks
                    unsubscribe();
                }
            });

            log('wp.data subscription for contact field pre-fill initialized');
        } catch (e) {
            // wp.data not available
        }
    }

    log('Contact field pre-fill initialization complete (v1.11.1)');
}

// Initialize module marker
export const MODULE_NAME = 'shipping-validator';
export const MODULE_VERSION = '1.11.2';
